import React, {
  lazy,
  Suspense,
  type FC,
  type ReactNode,
  useReducer,
  useState,
  useEffect,
  type PropsWithChildren,
} from 'react';

import styled from '@emotion/styled';
import { useSiteMetadata } from '@lucid/hooks';
import { getLanguage, getI18nData, formatLanguageKey, isBrowser } from '@lucid/utils';
import { graphql, useStaticQuery } from 'gatsby';
import {
  AnnouncementBanner,
  Box,
  LayoutContext,
  Pingdom,
  SiteFooter,
  type SiteFooterOptionalProps,
  type SiteFooterSocialLinks,
  GlobalCSS,
  ThemeProvider,
  ABTest,
  Delayed,
} from '@lucid/components';
import { theme } from '@lucid/theme';

import { useMenuFooter } from 'hooks/hooks';
import Navbar, { type NavbarOptionalProps } from 'components/navbar/navbar';
import type { Lucid } from 'types/types';

const CMSNodeButton = lazy(() => import('@lucid/components').then(module => ({ default: module.CMSNodeButton })));
const ABTestAdminUi = lazy(() => import('@lucid/components').then(module => ({ default: module.ABTestAdminUi })));
const PingdomHydration = lazy(() => import('@lucid/components').then(module => ({ default: module.Pingdom })));

interface ABTestAction {
  [key: string]: string;
}

interface ABTestObj {
  [key: string]: ABTestAction;
}

interface StaticQuery {
  allFeatureFlag: {
    nodes: Lucid.FeatureFlag.BaseConfig[];
  };
  allFooterSocialIconsJson: {
    edges: SiteFooterSocialLinks[];
  };
  i18nData: Lucid.I18n.DataEdges;
}

interface LayoutProps extends Lucid.Context.Layout {
  children: ReactNode;
  navbarProps?: NavbarOptionalProps;
  footerProps?: SiteFooterOptionalProps;
  hasNavbar?: boolean | null;
  hasFooter?: boolean | null;
}

const EmotionLayoutContextProvider = styled(LayoutContext.Provider)({});

const Layout: FC<PropsWithChildren<LayoutProps>> = ({
  shortCode,
  pageType,
  children,
  shouldHideSsr = false,
  nodeTranslations = [],
  nid = '',
  navbarProps,
  footerProps,
  hasNavbar = true,
  hasFooter = true,
  product,
}) => {
  const { i18nData } = useStaticQuery<StaticQuery>(graphql`
    query LayoutQuery {
      i18nData: allFile(filter: { sourceInstanceName: { eq: "i18n" } }) {
        edges {
          node {
            name
            childSrcJson {
              mktg_nav_mobile_toggle
              mktg_nav_mobile_toggle_suite
              mktg_footer_about_privacy
              mktg_footer_legal
              mktg_footer_language_switch
              mktg_nav_aria_label
              mktg_top_nav_aria_label
              mktg_nav_lang
              mktg_footer_nav_lang
              mktg_ct_cs_view_more
              mktg_footer_cookie_policy
              mktg_footer_cookie_privacy_choices
              mktg_blog_read_more
              mktg_announcement_lately_at_lucid_post
              mktg_chatbot_disclaimer
              mktg_chatbot_disclaimer_contact
              mktg_chatbot_disclaimer_expand
            }
          }
        }
      }
    }
  `);

  const [shouldShowShield, setShouldShowShield] = useState(shouldHideSsr);
  const [shouldShowEditButton, setShouldShowEditButton] = useState(false);
  const [shouldShowABTestButtons, setShouldShowABTestButtons] = useState(false);
  const { cmsUrl } = useSiteMetadata();
  const { locale_id, url_lang_prefix } = getLanguage(shortCode);
  const currentLangName = formatLanguageKey(shortCode, 'name');
  const i18n = getI18nData(i18nData, shortCode);

  const [abTests, updateTest] = useReducer((state: ABTestObj, action: ABTestAction) => {
    const currentABTestPropKeys = Object.keys(state);

    if (!currentABTestPropKeys.includes(action.testName)) {
      return {
        ...state,
        [action.testName]: action,
      };
    }

    if (currentABTestPropKeys.includes(action.testName)) {
      return {
        ...state,
        [action.testName]: {
          ...state[action.testName],
          ...action,
        },
      };
    }

    return state;
  }, {});

  useEffect(() => {
    if (isBrowser()) {
      setShouldShowEditButton(window.localStorage.getItem('showEditLink') === '1');
      setShouldShowABTestButtons(window.localStorage.getItem('showAbTestingUi') === '1');
    }
  }, []);

  useEffect(() => {
    const mobileThreshold = 768;
    const mobileTimeout = 4000;
    const desktopTimeout = 1000;
    const timeoutInMs = window.innerWidth >= mobileThreshold ? desktopTimeout : mobileTimeout;

    const safetyTimeoutId = setTimeout(() => {
      setShouldShowShield(false);
    }, timeoutInMs);

    return () => {
      clearTimeout(safetyTimeoutId);
    };
  }, [shouldHideSsr]);

  const layoutContext = {
    shortCode,
    urlLangPrefix: url_lang_prefix,
    localeId: locale_id,
    product,
    pageType,
    abTesting: {
      updateTest,
    },
  };

  // Footer props
  const footerMenu = useMenuFooter(shortCode) as Lucid.Directus.Generated.Footer_Column_Translations[];

  return (
    <EmotionLayoutContextProvider value={layoutContext}>
      <GlobalCSS />
      <Pingdom id="pingdom-check" />
      <Delayed>
        <Suspense fallback={<div key="pingdomHydrationFallback" id="pingdom-hydration" />}>
          {isBrowser() && (
            <PingdomHydration id="pingdom-hydration-check">PINGDOM_CANARY_STRING_HYDRATION</PingdomHydration>
          )}
        </Suspense>
      </Delayed>
      <ThemeProvider product={product} theme={theme}>
        <ABTest.Shield shouldShowShield={shouldShowShield} />
        {hasNavbar && (
          <Navbar
            {...navbarProps}
            buttonAriaLabel={i18n('mktg-footer-language-switch', {
              language: currentLangName,
            })}
            i18nData={i18nData}
            languageKey={shortCode}
            logo={{
              imageSrc: 'https://cdn-cashy-static-assets.lucid.co/lucid/logos/lucid.svg',
              imageAlt: 'Go to Lucid homepage',
            }}
            nid={nid}
            nodeTranslations={nodeTranslations}
            pageType={pageType}
            viewMoreText={i18n('mktg-ct-cs-view-more')}
          />
        )}
        <Box as="main" className="gatsby-root" data-lang={shortCode} data-nid={nid} data-page-type={pageType}>
          <AnnouncementBanner allowedPaths={['/']} i18nData={i18nData} product={product} />
          {children}
          <Suspense fallback={<div key="cmsNodeSuspenseFallback" id="cmsnode-suspense-fallback" />}>
            {shouldShowEditButton && (
              <CMSNodeButton href={`${cmsUrl}/admin/content/${pageType.replace(/-/gi, '_')}/${nid}`} />
            )}
          </Suspense>
          <Suspense fallback={<div key="abTestsSuspenseFallback" id="abtests-suspense-fallback" />}>
            {shouldShowABTestButtons && !!Object.keys(abTests).length && <ABTestAdminUi abTests={abTests as any} />}
          </Suspense>
        </Box>
        {hasFooter && (
          <SiteFooter {...footerProps} i18nData={i18nData} menu={footerMenu} nodeTranslations={nodeTranslations} />
        )}
      </ThemeProvider>
    </EmotionLayoutContextProvider>
  );
};

export default Layout;
